/* -------------------------- Design imports start -------------------------- */
import { Button, Stack } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useCallback, useState, useEffect } from "react";
import { flushSync } from 'react-dom'
import { toast } from 'react-toastify';
import { type BillInput, type Contract } from "../utils/types";
import { useTranslation } from 'react-i18next';
import { useCountContext, useUserContext } from '../../../utils/context';
import { PERMISSIONS } from '../../../config/permissions';
import LogTool from '../../../logger/logTools';
import GetConstant from '../utils/constants';
import SmartTable from '../../../components/SmartTable';
import { canDelete, isPrivileged } from '../../../utils/functions';
import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import DeleteModal from '../../../components/widgets/DeleteModal';
import BillStaffDrawer from '../Bill/BillStaffDrawer';
import { useContracts } from '../hooks/useContractQueries';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateBill,
  useDeleteContracts,
  usePatchContract,
} from '../hooks/useContractMutations';
import ContractDrawer from './ContractDrawer';
import ContractStaffDrawer from './ContractStaffDrawer';
import { ContractInfoPanelContent } from './ContractInfoPanel';
import { contractKeys } from '../utils/queryKeys';
import { markEntityAsRead } from '../../../api/unreadOverview';
import { type SmartTableColumnType, type FileCollection } from '../../../utils/types';
import { deriveFilesUrl } from '../../Filesystem/utils/filesHelper';
import { type ContractFilters } from '../api/contractApi';
import FilesDrawer from '../../Filesystem/components/FilesDrawer';
import useFetchExistingFiles from '../../../hooks/useFetchExistingFiles';
import { type FileInfo } from '../../../utils/types';
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  selectedContract: Contract | undefined
  setSelectedContract: (contract: Contract | undefined) => void
  openFilesDrawer: boolean
  setOpenFilesDrawer: (open: boolean) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start component                              */
/* -------------------------------------------------------------------------- */
export default function ContractOverviewPage(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'ContractOverviewPage', enable: true, logLevel: 'debug' })
  const { t } = useTranslation()
  const {
    selectedContract,
    setSelectedContract,
    openFilesDrawer,
    setOpenFilesDrawer,
  } = props
  const { user, userPermissions } = useUserContext()
  const {
    initialContracts,
    setInitialContracts,
    setInitialContractsCount,
    setCountTotals,
    contractQueryOptions,
    setContractQueryOptions,
  } = useCountContext()
  const queryClient = useQueryClient()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [contractDrawerOpen, setContractDrawerOpen] = useState(false)
  const [contractStaffDrawerOpen, setContractStaffDrawerOpen] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [billDrawerOpen, setBillDrawerOpen] = useState(false)
  const [filesDrawerRefreshKey, setFilesDrawerRefreshKey] = useState(0)
  /* ----------------------------- Flag states end ---------------------------- */

  /* --------------------------- Data states start ---------------------------- */
  const [selectedContracts, setSelectedContracts] = useState<Contract[]>([])
  const [contractFormInput, setContractFormInput] = useState<Contract | undefined>(undefined)
  const [contractStaffFormInput, setContractStaffFormInput] = useState<Contract | undefined>(undefined)
  const [billFormInput, setBillFormInput] = useState<BillInput | undefined>(undefined)
  const [newContracts, setNewContracts] = useState<Contract[]>(initialContracts)
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([])
  /* ---------------------------- Data states end ----------------------------- */

  const patchContractMutation = usePatchContract()
  const deleteContractsMutation = useDeleteContracts()
  const createBillMutation = useCreateBill()

  /* ------------------------------ Effects start ----------------------------- */
  // Fetch existing files when FilesDrawer opens directly (not from StaffDrawer)
  useFetchExistingFiles({
    open: false, // Main drawer is not open
    formState: { state: selectedContract },
    openFilesDrawer,
    setUploadedFiles,
    logContext: 'contract',
    resetOnClose: true,
  })
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------ Callback functions start ------------------------ */

  async function markContractAsRead(contractItem: Contract): Promise<void> {
    const contractId = Number(contractItem.key)
    if (Number.isNaN(contractId)) {
      log.warn('Unable to resolve contract id for marking as read', contractItem)
      return
    }

    // Optimistic update directly on all contract list queries
    queryClient.setQueriesData(
      { queryKey: contractKeys.lists(), exact: false },
      (oldData: any) => {
        if (!oldData || !oldData.results) return oldData
        return {
          ...oldData,
          results: oldData.results.map((item: Contract) =>
            item.key === contractItem.key ? { ...item, read: true } : item
          )
        }
      }
    )

    // Update counts for tab badges
    setInitialContractsCount(prev => Math.max(0, prev - 1))
    setCountTotals(prev => Math.max(0, prev - 1))

    try {
      await markEntityAsRead('contract', contractId)
    } catch (error) {
      log.error('Error marking contract as read', error)
      toast.error(t('contract:feedback.error.markContractAsRead', 'Fehler beim Aktualisieren des Lesestatus'))
    }
  }
  
  async function handleUpdateContract(input: Contract | undefined): Promise<string | undefined> {
    log.debug('handleUpdateContract', input)
    if (!input) {
      throw new Error('Error updating contract. No contract input provided')
    }
    try {
      const updatedContract = await patchContractMutation.mutateAsync({
        idOrSelf: input.self,
        data: input,
        expand: ['offer', 'editors', 'shipping_address', 'billing_address'], // Preserve expanded fields in cache
      })
      toast.success(
        t('contract:feedback.success.editContractSuccess', {
          defaultValue: 'Contract updated successfully.',
        })
      )
      setSelectedContracts([])
      setSelectedContract(undefined)
      // Don't set refreshKey here - it will be set after files are uploaded via onFilesUploaded callback
      return `${updatedContract.self}files/`
    } catch (error) {
      log.error('Error updating contract', error)
      toast.error(
        t('contract:feedback.error.editContractFailed', {
          defaultValue: 'Could not update contract.',
        })
      )
      setSelectedContracts([])
      setSelectedContract(undefined)
      return undefined
    }
  }

  const handleFilesUploaded = () => {
    // Trigger refresh of FilesDrawer after files are uploaded
    setFilesDrawerRefreshKey(prev => prev + 1)
  }

  async function handleDeleteContracts(): Promise<void> {
    const contractsToDelete = [...selectedContracts]

    // Clear selection before mutation to prevent warning about selected items
    // that are no longer visible (mutation's onSuccess invalidates queries)
    flushSync(() => {
      setSelectedContracts([])
      setSelectedContract(undefined)
      setOpenDelete(false)
    })

    try {
      await deleteContractsMutation.mutateAsync(contractsToDelete)
      toast.success(
        t('contract:feedback.success.deleteContractSuccess', {
          defaultValue: 'Selected contracts deleted.',
        })
      )
    } catch (error) {
      log.error(error)
      toast.error(
        t('contract:feedback.error.deleteContractFailed', {
          defaultValue: 'Could not delete selected contracts.',
        })
      )
    }
  }

  const handleBill = useCallback((event: Event, selectedContract: Contract): void => {
    log.debug('Creating Bill for contract -> ', selectedContract)
    event.stopPropagation()
    event.preventDefault()
    const newBill: /*BillInput*/any = {
      contract: selectedContract,
      amount: selectedContract?.price
        ? Object?.keys(selectedContract.price)
          .map(p => selectedContract?.price && selectedContract?.price[p])[0]
          ?.toString()
        : "0",
    }
    setBillFormInput(newBill)
    setBillDrawerOpen(true)
  }, [log])

  const handleCreateBill = async (input: BillInput): Promise<string | undefined> => {
    log.debug('HandleCreateBill -> ', input)
    try {
      const createdBill = await createBillMutation.mutateAsync(input)
      toast.success(
        t('contract:feedback.success.createBill', {
          defaultValue: 'Bill created successfully.',
        })
      )
      if (typeof createdBill.files === 'string') {
        return createdBill.files
      }
      if (createdBill.self) {
        return `${createdBill.self}files/`
      }
    } catch (error) {
      log.error('Error creating bill', error)
      toast.error(
        t('contract:feedback.error.createBill', {
          defaultValue: 'Could not create bill.',
        })
      )
    }
    return undefined
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // GetConstant calls hooks internally, so it cannot be inside useMemo
  const contractTableColumns = GetConstant({
    name: 'contractTableColumns',
    newContracts: newContracts,
    handleBill: handleBill,
  }) as SmartTableColumnType<Contract>[]

  const contractTableActions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {isPrivileged(user, 'STAFF') && (
        <Button
          id="editContractButton"
          size="small"
          variant="outlined"
          startIcon={<EditOutlined />}
          style={{ textTransform: 'none' }}
          onClick={() => {
            if (!isPrivileged(user, 'STAFF')) {
              const selectedContractItem: Contract = {
                ...selectedContracts[0],
                billingAddress: selectedContracts[0].billingAddress,
                shippingAddress: selectedContracts[0].shippingAddress,
                contractStatus: selectedContracts[0].contractStatus,
              }
              setContractFormInput(selectedContractItem)
              setContractDrawerOpen(true)
            } else {
              const selectedContractItem = { ...selectedContracts[0] }
              setContractStaffFormInput(selectedContractItem)
              setContractStaffDrawerOpen(true)
            }
          }}
          disabled={selectedContracts.length !== 1}
        >
          {t('contract:interaction.button.editContract')}
        </Button>
      )}
      {(canDelete(userPermissions, PERMISSIONS.CONTRACT.CONTRACT) || isPrivileged(user)) && (
        <Button
          id="deleteContractsButton"
          size="small"
          variant="outlined"
          style={{ textTransform: 'none' }}
          startIcon={<DeleteOutlined />}
          onClick={() => setOpenDelete(true)}
          disabled={
            selectedContracts.length === 0 ||
            selectedContracts.filter(contract => contract.contractStatus !== 8).length > 0
          }
        >
          {t('contract:interaction.button.deleteContracts')}
        </Button>
      )}
    </Stack>
  )

  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // collect files to be displayed by the FilesDrawer
  // Use uploadedFiles from useFetchExistingFiles if available, otherwise use files from selectedContract
  const collectedFiles: FileCollection[] = []
  if (selectedContract) {
    const contractFiles = uploadedFiles.length > 0 ? uploadedFiles : (Array.isArray(selectedContract.files) ? selectedContract.files : [])
    const contractFilesUrl = deriveFilesUrl(selectedContract)

    if (contractFiles.length > 0 || contractFilesUrl) {
      collectedFiles.push({
        title: t('common:content.label.contractFiles'),
        files: contractFiles,
        filesUrl: contractFilesUrl,
      })
    }
  }
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <SmartTable<Contract, ContractFilters>
        tableId="contractTable"
        columnsConfig={{
          allColumns: contractTableColumns,
          initialVisibleColumnIndices: [
            'key',
            'offer',
            'article',
            'organization',
            'price',
            'createdAt',
            'desiredDelivery',
            'latestDelivery',
            'priorityShipping',
            'editors',
            'contractStatus',
            'action',
          ],
        }}
        queryConfig={{
          dataHook: useContracts,
          initialQueryOptions: contractQueryOptions,
        }}
        queryState={{
          options: contractQueryOptions,
          setOptions: setContractQueryOptions,
        }}
        selection={{
          state: selectedContracts,
          setState: setSelectedContracts,
          onChange: (selected) => {
            if (selected.length === 1) {
              const record = selected[0]
              // Mark as read if unread
              if (!record.read) {
                void markContractAsRead(record)
              }
              setSelectedContract(record)
            } else if (selected.length === 0) {
              setSelectedContract(undefined)
            }
          },
        }}
        actions={contractTableActions}
        customInfoPanel={(open, setOpen) => (
          <ContractInfoPanelContent
            setOpenFilesDrawer={setOpenFilesDrawer}
            infoData={selectedContract}
          />
        )}
        onRow={{
          onClick: (record) => {
            setSelectedContracts([record])
            setSelectedContract(record)
          },
        }}
        contextMenuOptions={[
          {
            label: t('common:interaction.button.showFiles'),
            onClick: (record) => {
              setSelectedContract(record)
              setOpenFilesDrawer(true)
            },
          },
        ]}
      />
      <ContractDrawer
        open={contractDrawerOpen}
        setOpen={setContractDrawerOpen}
        formState={{
          state: contractFormInput,
          setState: setContractFormInput,
        }}
        enableEditMode={true}
      />
      {isPrivileged(user, 'STAFF') && (
        <BillStaffDrawer
          open={billDrawerOpen}
          setOpen={setBillDrawerOpen}
          formState={{
            state: billFormInput,
            setState: setBillFormInput,
          }}
          onCreateConfirm={handleCreateBill}
        />
      )}
      {isPrivileged(user, 'STAFF') && contractStaffFormInput && (
        <ContractStaffDrawer
          open={contractStaffDrawerOpen}
          setOpen={setContractStaffDrawerOpen}
          formState={{
            state: {
              ...contractStaffFormInput,
              contractStatus: contractStaffFormInput.contractStatus,
            },
            setState: setContractStaffFormInput,
          }}
          onEditConfirm={handleUpdateContract}
          onFilesUploaded={handleFilesUploaded}
        />
      )}
      <DeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        onDelete={handleDeleteContracts}
        name={t('common:content.label.contracts')}
      />
      {openFilesDrawer && (
        <FilesDrawer
          open={openFilesDrawer}
          setOpen={setOpenFilesDrawer}
          files={collectedFiles}
          title={t('common:content.label.files')}
          refreshKey={filesDrawerRefreshKey}
        />
      )}
    </>
  )
}
